import React, { useState, useEffect, useContext } from 'react';
import {
    Typography,
    AppBar,
    Toolbar,
    Box,
    Grid,
    Container,
    Stack,
    Divider,
    Button,
    Snackbar,
    Alert,
} from '@mui/material';
import { ArrowBackIos, HolidayVillage, FileDownload } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { BackDropLoading } from '../../ui-component';
import { ProfileContext } from '../../contexts/ProfileContext';
import { ChipRoomInfo } from '../../ui-component/ChipRoomInfo';
import * as dict from '../../dictionary';
import * as api from '../../api/Documents';

export const Documents = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const context = useContext(ProfileContext);
    
    const [loadingBackDrop, setLoadingBackDrop] = useState(false);
    const [profile, setProfile] = useState(null);
    const [houseInfo, setHouseInfo] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [openSnackbar, setOpenSnackbar] = useState(false);

    // Get house info from localStorage as fallback
    const houseInfoSelected = localStorage.getItem("houseInfoSelected");
    const [houseInfoParse] = useState(JSON.parse(houseInfoSelected));

    useEffect(() => {
        if (context.state?.profile !== null) {
            setProfile(context.state?.profile?.customer[0]);
        }

        if (context.state?.houseInfo !== null) {
            setHouseInfo(context.state?.houseInfo);
        }
    }, [context]);

    const handleDownload = async (contractId, documentType, file_name, base_url) => {
        setLoadingBackDrop(true)
        try {
            
        if (!contractId || !documentType) {
            console.error('Invalid contractId or documentType');
            return;
        }
        const documentPath = `DocumentHanding/th/document_openbycont.aspx?c=${encodeURIComponent(contractId)}&dt=${encodeURIComponent(documentType)}`
        const documentUrl = base_url ? `${base_url}/${documentPath}` : `https://192ryl-rental-test.iconframework.com/uat/${documentPath}`
        const response = await api.getDocumentFile({
            base_url: documentUrl,
            file_name: file_name 
        });
        if (response.status !== 200) {
            setOpenSnackbar(true)
            return;
        }
        const fileUrl = response.data.url
        window.open(fileUrl);
        // window.open(`https://192ryl-rental-test.iconframework.com/uat/DocumentHanding/th/document_openbycont.aspx?c=${contractId}&dt=${documentType}`)

        } catch (error) {
            console.error('Download error:', error);
        } finally {
        setLoadingBackDrop(false)
        }
    };

    const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpenSnackbar(false);
    };

    useEffect(() => {
        const fetchDocuments = async () => {
            if (houseInfo || houseInfoParse) {
                try {
                    setLoadingBackDrop(true);
                    const info = houseInfo || houseInfoParse;


                    const response = await api.getDocumentsByProject({
                        line_id: profile.line_id,
                        contract_id: info.contract_id,
                        project_id: info.project_id,
                        product_type: info.product_type
                    })
                    
                    if (response.data?.data) {
                        const documents = response.data.data;
                        
                        const groupedDocuments = documents.reduce((acc, doc, index) => {
                            const group = acc.find(g => g.company_id === doc.company_id);
                            if (group) {
                                group.documents.push({
                                    id: index,
                                    doctype: doc.doc_name,
                                    status: doc.file_id && doc.file_name ? 1 : 0,
                                    contract_id: doc.contract_id,
                                    file_id: doc.file_id,
                                    file_name: doc.file_name,
                                    base_url: doc.company_base_url
                                });
                            } else {
                                acc.push({
                                    company_id: doc.company_id,
                                    company_name: profile?.language === 'en' ? doc.company_name_eng : doc.company_name_thai,
                                    documents: [{
                                        id: index,
                                        doctype: doc.doc_name,
                                        status: doc.file_id && doc.file_name ? 1 : 0,
                                        contract_id: doc.contract_id,
                                        file_id: doc.file_id,
                                        file_name: doc.file_name,
                                        base_url: doc.company_base_url
                                    }]
                                });
                            }
                            return acc;
                        }, []);
                        setDocuments(groupedDocuments);
                    }
                } catch (error) {
                    console.error('Error fetching documents:', error);
                } finally {
                    setLoadingBackDrop(false);
                }
            }
        };

        fetchDocuments();
    }, [houseInfo, houseInfoParse]);

    const CompanyDocuments = ({ group }) => (
        <Box>
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                {group.company_name}
            </Typography>
            <Stack spacing={2} sx={{ pl: 2 }}>
                {group.documents.map((doc) => (
                    <Stack 
                        key={doc.id} 
                        direction="row" 
                        justifyContent="space-between" 
                        alignItems="center"
                        sx={{ 
                            borderBottom: '1px solid #eee',
                            pb: 1
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography 
                                variant="body1" 
                                sx={{ 
                                    mr: 1,
                                    color: doc.status === 1 ? 'inherit' : 'text.disabled'
                                }}
                            >
                                - {doc.doctype}
                            </Typography>
                        </Box>
                        <Button
                            startIcon={<FileDownload />}
                            color="primary"
                            onClick={() => handleDownload(doc.contract_id, doc.doctype, doc.file_name, doc.base_url)}
                            size="small"
                            disabled={doc.status !== 1}
                            sx={{
                                opacity: doc.status === 1 ? 1 : 0.5
                            }}
                        >
                            {/* {dict?.Download_TXT?.[profile?.language] || "ดาวน์โหลด"} */}
                        </Button>
                    </Stack>
                ))}
            </Stack>
        </Box>
    );

    return (
        <Box sx={{ height: "calc(100vh - 60px)" }}>
            {/* Header */}
            <AppBar sx={{
                position: 'fixed',
                boxShadow: 'none',
                height: 60,
            }}>
                <Toolbar>
                    <Grid container item xs={1}>
                        <ArrowBackIos onClick={() => navigate(`/myhome${location.search}`)} />
                    </Grid>
                    <Grid container item xs={10} direction="row"
                        justifyContent="center"
                    >
                        <Typography>
                            {location.state?.title || "เอกสาร"}
                        </Typography>
                    </Grid>
                </Toolbar>
            </AppBar>

            {/* Main Content */}
            <Container maxWidth="xl" sx={{ mb: 5 }}>
                <BackDropLoading loading={loadingBackDrop} />

                {/* House/Unit Info */}
                <Stack direction={"row"} alignItems="center" justifyContent={"flex-start"}>
                    <ChipRoomInfo
                        houseInfo={houseInfo}
                        houseInfoParse={houseInfoParse}
                        icon={<HolidayVillage />}
                        lang={profile?.language}
                        sx={{ mt: 17 }}
                    />
                </Stack>

                {/* Document List Section */}
                <Box sx={{ marginTop: 3 }}>
                    <Divider textAlign="left">
                        <Typography variant='subtitle1'>{dict?.Documents_Header_TXT[profile?.language]}</Typography>
                    </Divider>
                </Box>

                <Stack mt={2} spacing={3}>
                    {documents.map((group, index) => (
                        <CompanyDocuments key={index} group={group} />
                    ))}
                </Stack>

            </Container>

            <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
            <Alert severity="error">{dict?.Error_Download_TXT[profile?.language]}</Alert>
            </Snackbar>
        </Box>
    );
};
